import type { Judgement, NoteResult } from '../analysis/analyze';

export interface ScoreOptions {
  /** Points per judgement, 0 to 1. */
  points: Record<Judgement, number>;
  /** Weight of pitch in the overall score; rhythm gets the rest. */
  pitchWeight: number;
  /** Bars scoring below this (0 to 1) are listed as weak. */
  weakBarBelow: number;
  /** How many weak bars to list. */
  weakBarCount: number;
}

export const DEFAULT_SCORE_OPTIONS: ScoreOptions = {
  points: { good: 1, close: 0.5, wrong: 0 },
  pitchWeight: 0.6,
  weakBarBelow: 0.85,
  weakBarCount: 3,
};

export interface TakeScores {
  /** 0 to 100. */
  overall: number;
  pitch: number;
  /** Null when no onset could be judged (e.g. everything legato on one pitch). */
  rhythm: number | null;
  noteCount: number;
  pitchGood: number;
  pitchClose: number;
  pitchWrong: number;
  missed: number;
  rhythmJudged: number;
  late: number;
  early: number;
}

export interface BarIssues {
  missed: number;
  flat: number;
  sharp: number;
  octave: number;
  late: number;
  early: number;
  /** Mean cents of the flat notes (negative), or null. */
  flatCents: number | null;
  /** Mean cents of the sharp notes, or null. */
  sharpCents: number | null;
}

export interface WeakBar {
  timelineMeasureIndex: number;
  measureIndex: number;
  measureNumber: string;
  /** 0 to 1. */
  score: number;
  noteCount: number;
  issues: BarIssues;
}

export interface TakeScoring {
  scores: TakeScores;
  weakestBars: WeakBar[];
}

export type SuggestedGrade = 'READY' | 'ALMOST' | 'NEEDS_WORK';

export interface GradeThresholds {
  ready: number;
  almost: number;
}

export const DEFAULT_GRADE_THRESHOLDS: GradeThresholds = { ready: 85, almost: 70 };

/** A wrong note within this many cents of an octave above or below is an octave error. */
const OCTAVE_MATCH_CENTS = 50;

function notePoints(r: NoteResult, o: ScoreOptions): number {
  if (r.status === 'missed') return 0;
  const pitch = o.points[r.pitch as Judgement];
  if (r.rhythm === null) return pitch;
  return o.pitchWeight * pitch + (1 - o.pitchWeight) * o.points[r.rhythm];
}

function issuesOf(results: readonly NoteResult[]): BarIssues {
  const issues: BarIssues = { missed: 0, flat: 0, sharp: 0, octave: 0, late: 0, early: 0, flatCents: null, sharpCents: null };
  const flat: number[] = [];
  const sharp: number[] = [];
  for (const r of results) {
    if (r.status === 'missed') {
      issues.missed++;
      continue;
    }
    if (r.pitch !== 'good' && r.cents !== null) {
      const folded = r.cents - 1200 * Math.round(r.cents / 1200);
      if (Math.abs(r.cents) >= 1100 && Math.abs(folded) <= OCTAVE_MATCH_CENTS) issues.octave++;
      else if (r.cents < 0) flat.push(r.cents);
      else sharp.push(r.cents);
    }
    if (r.rhythm !== null && r.rhythm !== 'good' && r.onsetMs !== null) {
      if (r.onsetMs > 0) issues.late++;
      else issues.early++;
    }
  }
  issues.flat = flat.length;
  issues.sharp = sharp.length;
  const mean = (xs: number[]) => (xs.length ? Math.round(xs.reduce((s, x) => s + x, 0) / xs.length) : null);
  issues.flatCents = mean(flat);
  issues.sharpCents = mean(sharp);
  return issues;
}

/**
 * Overall, pitch and rhythm scores from 0 to 100, and the weakest bars. Missed notes count as
 * wrong for pitch; rhythm is judged only on notes whose onset could be heard.
 */
export function scoreTake(results: readonly NoteResult[], options: Partial<ScoreOptions> = {}): TakeScoring {
  const o = { ...DEFAULT_SCORE_OPTIONS, ...options };
  const n = results.length;
  let pitchPoints = 0;
  let rhythmPoints = 0;
  const s: TakeScores = {
    overall: 0,
    pitch: 0,
    rhythm: null,
    noteCount: n,
    pitchGood: 0,
    pitchClose: 0,
    pitchWrong: 0,
    missed: 0,
    rhythmJudged: 0,
    late: 0,
    early: 0,
  };
  for (const r of results) {
    if (r.pitch === 'missed') s.missed++;
    else {
      if (r.pitch === 'good') s.pitchGood++;
      else if (r.pitch === 'close') s.pitchClose++;
      else s.pitchWrong++;
      pitchPoints += o.points[r.pitch];
    }
    if (r.rhythm !== null) {
      s.rhythmJudged++;
      rhythmPoints += o.points[r.rhythm];
      if (r.rhythm !== 'good' && r.onsetMs !== null) {
        if (r.onsetMs > 0) s.late++;
        else s.early++;
      }
    }
  }
  s.pitch = n > 0 ? Math.round((pitchPoints / n) * 100) : 0;
  s.rhythm = s.rhythmJudged > 0 ? Math.round((rhythmPoints / s.rhythmJudged) * 100) : null;
  s.overall = s.rhythm === null ? s.pitch : Math.round(o.pitchWeight * s.pitch + (1 - o.pitchWeight) * s.rhythm);

  // Weakest bars: per played bar (a repeated bar counts per pass).
  const byBar = new Map<number, NoteResult[]>();
  for (const r of results) {
    const list = byBar.get(r.timelineMeasureIndex);
    if (list) list.push(r);
    else byBar.set(r.timelineMeasureIndex, [r]);
  }
  const bars: WeakBar[] = [];
  for (const [timelineMeasureIndex, list] of byBar) {
    const score = list.reduce((sum, r) => sum + notePoints(r, o), 0) / list.length;
    if (score >= o.weakBarBelow) continue;
    const first = list[0] as NoteResult;
    bars.push({
      timelineMeasureIndex,
      measureIndex: first.measureIndex,
      measureNumber: first.measureNumber,
      score: Math.round(score * 1000) / 1000,
      noteCount: list.length,
      issues: issuesOf(list),
    });
  }
  bars.sort((a, b) => a.score - b.score || a.timelineMeasureIndex - b.timelineMeasureIndex);
  return { scores: s, weakestBars: bars.slice(0, o.weakBarCount) };
}

/** The grade the app suggests to the director (Hazır / Neredeyse / Çalışmalı). */
export function suggestGrade(overall: number, thresholds: GradeThresholds = DEFAULT_GRADE_THRESHOLDS): SuggestedGrade {
  if (overall >= thresholds.ready) return 'READY';
  if (overall >= thresholds.almost) return 'ALMOST';
  return 'NEEDS_WORK';
}
